import Phaser from 'phaser';
import ambientJson from '../../../recipes/art/painted/ambient.json' with { type: 'json' };

import mysteryAmbient from '../../../recipes/art/painted/mystery/ambient.json' with { type: 'json' };

type Point = readonly [number, number];
/** The painting's own marks: lamps, fires (its smoke points) and water. */
export interface AmbientPlate { id: string; lamps: readonly Point[]; smoke: readonly Point[]; water: readonly (readonly Point[])[] }
/** Falling water or poured grain: streaks run `from` the lip `to` the foot, where foam (or dust) spreads. */
interface Fall { from: Point; to: Point; width: number; kind: 'water' | 'grain' }
/** A flame's base on the painting and its size (1 is a brazier). */
type Flame = readonly [number, number, number];
/** Fire the story sets: lit once `when` holds (the host decides, by the world facts). */
export interface Burning { id: string; when: { all: string[]; any: string[]; none: string[] }; flames: readonly Flame[] }
/** Per place: dust motes drifting in lamplight (lit interiors), mist over the water (0 to 1), falls and chutes,
 * currents (a region of the painting whose own pixels flow, px/s on screen, with an optional swell in px), flames (replacing one per smoke point) and burnings. */
interface Current { flow: Point; polygon: Point[]; swell?: number; exclude?: Point[][] }
/** A shaft through an opening: half-widths at source/foot, visible air and actor exposure are separate. */
interface Daylight { from: Point; to: Point; width: Point; colour: readonly [number, number, number]; opacity: number; exposure: number; motes: number }
interface Plume { from: Point; drift: Point; size: number; opacity?: number; spread?: number }
interface Hearth { polygon: Point[]; strength: number }
interface Heat { at: Point; width: number; height: number; strength: number }
interface Place { motes: number; mist: number; falls?: Fall[]; currents?: Current[]; flames?: Flame[]; burning?: Burning[]; daylight?: Daylight[]; moths?: Point[]; plumes?: Plume[]; heat?: Heat[]; hearths?: Hearth[] }
const PLACES = { ...ambientJson.places, ...mysteryAmbient.places } as unknown as Readonly<Record<string, Place>>;
/** What the story can set alight in this place. */
export function burnings(place: string): readonly Burning[] { return PLACES[place]?.burning ?? []; }

interface Lamp { image: Phaser.GameObjects.Image; base: number; scale: number; swing: number; rates: [number, number, number]; phases: [number, number, number]; gutter: number }
interface Particle {
  image: Phaser.GameObjects.Image; kind: 'ember' | 'puff' | 'mote' | 'glint' | 'drop' | 'foam' | 'dust';
  from: Point; x: number; y: number; vx: number; vy: number; age: number; life: number; size: number; peak: number; phase: number;
  polygon?: readonly Point[]; fall?: Fall; daylight?: Daylight; plume?: Plume;
}
/** A region of the painting redrawn each frame from its own pixels, scrolled along the flow in two copies half a cycle apart
 * and cross-faded (a scroll blend), under a soft mask: the painted water itself runs. */
interface Flow {
  image: Phaser.GameObjects.Image; texture: Phaser.Textures.CanvasTexture; source: HTMLCanvasElement; mask: HTMLCanvasElement;
  along: Point; cycle: number; speed: number; swell: number; span: number; slow: boolean;
}
/** One tongue of a flame: rooted at its base, it stretches, narrows and bends on its own noise. */
interface Tongue { image: Phaser.GameObjects.Image; x: number; y: number; width: number; height: number; alpha: number; rates: [number, number, number]; phases: [number, number, number] }
interface Drift { image: Phaser.GameObjects.Image; left: number; span: number; y: number; speed: number; offset: number; alpha: number }

function seeded(text: string): () => number {
  let a = 0;
  for (let i = 0; i < text.length; i++) a = Math.imul(a ^ text.charCodeAt(i), 2654435761);
  return () => { // mulberry32
    a = a + 0x6D2B79F5 | 0; let t = Math.imul(a ^ a >>> 15, 1 | a);
    t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t; return ((t ^ t >>> 14) >>> 0) / 4294967296;
  };
}
function inside([x, y]: Point, polygon: readonly Point[]): boolean {
  let hit = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [ax, ay] = polygon[i]!, [bx, by] = polygon[j]!;
    if ((ay > y) !== (by > y) && x < (bx - ax) * (y - ay) / (by - ay) + ax) hit = !hit;
  }
  return hit;
}
function bounds(polygon: readonly Point[]): { left: number; top: number; right: number; bottom: number } {
  let left = Infinity, top = Infinity, right = -Infinity, bottom = -Infinity;
  for (const [x, y] of polygon) { left = Math.min(left, x); top = Math.min(top, y); right = Math.max(right, x); bottom = Math.max(bottom, y); }
  return { left, top, right, bottom };
}
/** One feather for both the visible air and the people walking through it. */
function daylightWeight(light: Daylight, x: number, y: number): number {
  const dx = light.to[0] - light.from[0], dy = light.to[1] - light.from[1], length2 = dx * dx + dy * dy;
  if (!length2) return 0;
  const px = x - light.from[0], py = y - light.from[1], along = (px * dx + py * dy) / length2;
  if (along <= 0 || along >= 1) return 0;
  const width = light.width[0] + (light.width[1] - light.width[0]) * along;
  const across = Math.abs(px * dy - py * dx) / Math.sqrt(length2);
  const edge = Math.max(0, 1 - across / width);
  return edge * edge * Math.min(1, along * 8, (1 - along) * 5);
}
function radial(scene: Phaser.Scene, key: string, width: number, height: number, stops: readonly [number, string][]): void {
  if (scene.textures.exists(key)) return;
  const texture = scene.textures.createCanvas(key, width, height)!, c = texture.context, r = Math.max(width, height) / 2;
  c.translate(width / 2, height / 2); c.scale(width / 2 / r, height / 2 / r);
  const gradient = c.createRadialGradient(0, 0, 0, 0, 0, r);
  for (const [at, colour] of stops) gradient.addColorStop(at, colour);
  c.fillStyle = gradient; c.fillRect(-r, -r, r * 2, r * 2); texture.refresh();
}

/** A soft tongue of flame, pointed at the top and full at its base, in one colour. */
function flameTexture(scene: Phaser.Scene, key: string, rgb: string): void {
  if (scene.textures.exists(key)) return;
  const texture = scene.textures.createCanvas(key, 20, 64)!, c = texture.context, gradient = c.createLinearGradient(0, 0, 0, 64);
  gradient.addColorStop(0, `rgba(${rgb},0)`); gradient.addColorStop(.3, `rgba(${rgb},.55)`); gradient.addColorStop(.75, `rgba(${rgb},1)`); gradient.addColorStop(1, `rgba(${rgb},.7)`);
  // A thin curling tip over a full base: the shape reads as a tongue even when small.
  c.filter = 'blur(.7px)'; c.fillStyle = gradient; c.beginPath(); c.moveTo(11, 1);
  c.bezierCurveTo(12, 18, 19, 34, 18, 50); c.quadraticCurveTo(17, 62, 10, 62); c.quadraticCurveTo(3, 62, 2, 50); c.bezierCurveTo(2, 36, 9, 22, 11, 1);
  c.fill(); texture.refresh();
}

/** One quiet motion layer over a painting: flickering lamps and flames, embers and smoke, dust in lit rooms,
 * glints, currents and mist on water, falls and poured grain, and fire where the story burned something. Display-only; it stills under reduced motion and holds while time is held. */
export class Ambient {
  #scene: Phaser.Scene;
  #objects: Phaser.GameObjects.GameObject[] = [];
  #lamps: Lamp[] = [];
  #particles: Particle[] = [];
  #ripples: { image: Phaser.GameObjects.Image; left: number; index: number }[] = [];
  #mist: Drift[] = [];
  #tongues: Tongue[] = [];
  #flows: Flow[] = [];
  #heat: { image: Phaser.GameObjects.Image; texture: Phaser.Textures.CanvasTexture; source: HTMLCanvasElement; mask: HTMLCanvasElement; strength: number }[] = [];
  #hearths: { image: Phaser.GameObjects.Image; texture: Phaser.Textures.CanvasTexture; source: HTMLCanvasElement; mask: HTMLCanvasElement; strength: number }[] = [];
  #moths: { image: Phaser.GameObjects.Image; at: Point; phase: number }[] = [];
  /** Each flow's cut of its painting and soft mask, made once per plate and kept. */
  #cuts = new Map<string, { source: HTMLCanvasElement; mask: HTMLCanvasElement }>();
  #place: Place = { motes: 0, mist: 0 };
  #lit = new Set<string>();
  #add: (x: number, y: number, key: string, depth: number, blend?: number) => Phaser.GameObjects.Image = () => { throw new Error('No painting entered'); };
  #random = seeded('');
  #time = 0;
  #frame = 0;
  #still = false;

  constructor(scene: Phaser.Scene) {
    this.#scene = scene;
    radial(scene, 'painted-glow', 128, 128, [[0, 'rgba(255,184,75,.8)'], [.2, 'rgba(255,138,45,.25)'], [1, 'rgba(255,120,35,0)']]);
    radial(scene, 'ambient-ember', 8, 8, [[0, 'rgba(255,236,170,1)'], [.35, 'rgba(255,150,50,.9)'], [1, 'rgba(255,90,20,0)']]);
    radial(scene, 'ambient-puff', 64, 64, [[0, 'rgba(176,170,160,.55)'], [.5, 'rgba(160,156,148,.25)'], [1, 'rgba(150,146,140,0)']]);
    radial(scene, 'ambient-mote', 8, 8, [[0, 'rgba(255,232,196,1)'], [.4, 'rgba(255,214,160,.5)'], [1, 'rgba(255,200,140,0)']]);
    radial(scene, 'ambient-glint', 16, 4, [[0, 'rgba(255,244,214,1)'], [.45, 'rgba(236,196,130,.45)'], [1, 'rgba(200,170,120,0)']]);
    // Colours are baked per texture: the shipped renderer is Canvas2D, which does not tint.
    for (const [kind, rgb, foam] of [['water', '220,235,240', '228,236,238'], ['grain', '250,222,160', '214,184,130']] as const) {
      radial(scene, `ambient-drop-${kind}`, 3, 22, [[0, `rgba(${rgb},1)`], [.5, `rgba(${rgb},.55)`], [1, `rgba(${rgb},0)`]]);
      radial(scene, `ambient-foam-${kind}`, 32, 32, [[0, `rgba(${foam},.9)`], [.5, `rgba(${foam},.35)`], [1, `rgba(${foam},0)`]]);
    }
    ['255,84,22', '255,146,40', '255,208,96', '255,244,200'].forEach((rgb, i) => flameTexture(scene, `ambient-flame-${i}`, rgb));
    radial(scene, 'ambient-smoke', 128, 128, [[0, 'rgba(70,64,58,.65)'], [.5, 'rgba(66,60,54,.32)'], [1, 'rgba(60,56,50,0)']]);
    radial(scene, 'ambient-scorch', 64, 32, [[0, 'rgba(12,8,6,.75)'], [.6, 'rgba(18,12,8,.4)'], [1, 'rgba(20,14,10,0)']]);
    radial(scene, 'ambient-mist', 256, 64, [[0, 'rgba(196,204,208,.5)'], [.55, 'rgba(186,194,200,.22)'], [1, 'rgba(180,188,194,0)']]);
    const moth = scene.textures.createCanvas('ambient-moth', 12, 8)!, c = moth.context;
    c.fillStyle = '#b7a687';
    for (const x of [3, 9]) { c.beginPath(); c.ellipse(x, 4, 3, 2, x < 6 ? -.45 : .45, 0, Math.PI * 2); c.fill(); }
    c.fillStyle = '#544b3e'; c.fillRect(5, 2, 2, 5); moth.refresh();
  }

  /** Build this painting's motion. The previous painting's is destroyed. */
  enter(plate: AmbientPlate): void {
    const scene = this.#scene, place = PLACES[plate.id] ?? { motes: 0, mist: 0 };
    this.#objects.forEach(object => object.destroy());
    this.#objects = []; this.#lamps = []; this.#particles = []; this.#ripples = []; this.#mist = []; this.#tongues = []; this.#flows = []; this.#heat = []; this.#hearths = []; this.#moths = []; this.#lit.clear();
    this.#place = place;
    const random = this.#random = seeded(plate.id), between = (a: number, b: number): number => a + (b - a) * random();
    const add = this.#add = (x: number, y: number, key: string, depth: number, blend = Phaser.BlendModes.ADD): Phaser.GameObjects.Image => {
      const image = scene.add.image(x, y, key).setDepth(depth).setBlendMode(blend);
      this.#objects.push(image); return image;
    };
    // Each flame breathes on its own slow swell, with a quicker flutter and the odd gutter: never a shared pulse.
    const flame = (at: Point, base: number, scale: number, swing: number): void => {
      this.#lamps.push({ image: add(at[0], at[1], 'painted-glow', 2000).setScale(scale).setAlpha(base), base, scale, swing,
        rates: [between(1.3, 2.4), between(3.6, 6.2), between(31, 53)], phases: [between(0, 6.3), between(0, 6.3), between(0, 6.3)], gutter: 0 });
    };
    for (const lamp of plate.lamps) flame(lamp, .2, .65, 1);
    for (const fire of plate.smoke) {
      flame(fire, .2, .42, 1.6);
      for (let i = 0; i < 5; i++) this.#spawn({ kind: 'ember', from: fire, image: add(fire[0], fire[1], 'ambient-ember', 2002) }, -between(0, 1.8));
      for (let i = 0; i < 3; i++) this.#spawn({ kind: 'puff', from: fire, image: add(fire[0], fire[1], 'ambient-puff', 2002, Phaser.BlendModes.NORMAL) }, -i * 1.7 - between(0, .6));
    }
    for (const at of place.flames ?? plate.smoke.map(([x, y]): Flame => [x, y + 6, .7])) this.#flame(at);
    // A working furnace has established smoke when we arrive. Stagger overlapping
    // strands across their lifetimes rather than spending the first seconds filling it.
    for (const plume of place.plumes ?? []) for (let i = 0; i < 8; i++) {
      this.#spawn({ kind: 'puff', from: plume.from, plume,
        image: add(plume.from[0], plume.from[1], 'ambient-smoke', 2002, Phaser.BlendModes.NORMAL) }, 0);
      const particle = this.#particles.at(-1)!;
      particle.age = particle.life * i / 8;
      particle.x += particle.vx * particle.age; particle.y += particle.vy * particle.age;
    }
    place.hearths?.forEach((hearth, index) => this.#hearth(plate.id, index, hearth));
    place.heat?.forEach((heat, index) => this.#heatHaze(plate.id, index, heat));
    for (const fall of place.falls ?? []) {
      const water = fall.kind === 'water', drops = Math.round(fall.width / (water ? 2.2 : 1.8)), foam = Math.round(fall.width / 4) + 4;
      for (let i = 0; i < drops; i++) this.#spawn({ kind: 'drop', from: fall.from, fall, image: add(0, 0, `ambient-drop-${fall.kind}`, 2001, water ? Phaser.BlendModes.ADD : Phaser.BlendModes.NORMAL) }, -between(0, 1));
      for (let i = 0; i < foam; i++) this.#spawn({ kind: 'foam', from: fall.to, fall, image: add(0, 0, `ambient-foam-${fall.kind}`, 2001, Phaser.BlendModes.NORMAL) }, -between(0, 1.6));
    }
    place.currents?.forEach((current, index) => this.#flow(plate.id, index, current));
    place.daylight?.forEach((light, index) => this.#daylight(plate.id, index, light));
    for (const at of place.moths ?? []) this.#moths.push({ at, phase: random() * 6.3,
      image: add(at[0], at[1], 'ambient-moth', 2002, Phaser.BlendModes.NORMAL).setAlpha(.65) });
    for (let i = 0; i < place.motes && plate.lamps.length; i++) {
      const lamp = plate.lamps[Math.floor(random() * plate.lamps.length)]!;
      this.#spawn({ kind: 'mote', from: lamp, image: add(lamp[0], lamp[1], 'ambient-mote', 2000) }, -between(0, 8));
    }
    plate.water.forEach((polygon, index) => {
      const box = bounds(polygon), width = Math.ceil(box.right - box.left), height = Math.ceil(box.bottom - box.top), key = `${plate.id}:water:${index}`;
      if (!scene.textures.exists(key)) {
        const texture = scene.textures.createCanvas(key, width, height)!, context = texture.context;
        context.beginPath();
        polygon.forEach(([x, y], i) => i ? context.lineTo(x - box.left, y - box.top) : context.moveTo(x - box.left, y - box.top));
        context.closePath(); context.clip(); context.lineWidth = .8;
        for (let i = 0; i < 90; i++) {
          const x = (i * 83.71) % width, y = (i * 37.43) % height;
          context.strokeStyle = i % 5 ? 'rgba(119,151,159,.14)' : 'rgba(227,163,94,.18)';
          context.beginPath(); context.moveTo(x, y); context.lineTo(x + 4 + i % 9, y); context.stroke();
        }
        texture.refresh();
      }
      this.#ripples.push({ image: add(box.left, box.top, key, 2001, Phaser.BlendModes.NORMAL).setOrigin(0), left: box.left, index });
      const glints = Math.max(3, Math.min(18, Math.round(width * height / 9000)));
      for (let i = 0; i < glints; i++) this.#spawn({ kind: 'glint', from: [box.left, box.top], polygon, image: add(box.left, box.top, 'ambient-glint', 2001) }, -between(0, 1.4));
      // Low mist lies on the water and drifts across it, fading at either end of its path.
      for (let i = 0; i < 3 && place.mist > 0; i++) {
        const image = add(0, 0, 'ambient-mist', 2001, Phaser.BlendModes.NORMAL).setScale(Math.max(.5, width * .55 / 256), Math.max(.6, Math.min(1.6, height / 90)));
        this.#mist.push({ image, left: box.left - image.displayWidth * .3, span: width + image.displayWidth * .6, y: box.top + height * between(.35, .85),
          speed: between(4, 9), offset: random(), alpha: place.mist * .42 });
      }
    });
    this.#still = false;
    this.tick(0, false, true);
  }

  /** Cached translucent air, confined to the painting's opening. No per-frame pixel work. */
  #daylight(plate: string, index: number, light: Daylight): void {
    const key = `${plate}:daylight:${index}`, scene = this.#scene, margin = Math.max(...light.width);
    const left = Math.floor(Math.min(light.from[0], light.to[0]) - margin), top = Math.floor(Math.min(light.from[1], light.to[1]) - margin);
    const width = Math.ceil(Math.abs(light.to[0] - light.from[0]) + margin * 2), height = Math.ceil(Math.abs(light.to[1] - light.from[1]) + margin * 2);
    if (!scene.textures.exists(key)) {
      // Soft air has no high-frequency detail: half-resolution costs a quarter of the pixels.
      const texture = scene.textures.createCanvas(key, Math.ceil(width / 2), Math.ceil(height / 2))!, c = texture.context;
      const pixels = c.createImageData(texture.width, texture.height);
      for (let y = 0; y < texture.height; y++) for (let x = 0; x < texture.width; x++) {
        const at = (y * texture.width + x) * 4;
        pixels.data[at] = light.colour[0]; pixels.data[at + 1] = light.colour[1]; pixels.data[at + 2] = light.colour[2];
        pixels.data[at + 3] = 255 * daylightWeight(light, left + x * 2, top + y * 2);
      }
      c.putImageData(pixels, 0, 0); texture.refresh();
    }
    this.#add(left, top, key, 1999, Phaser.BlendModes.SCREEN).setOrigin(0).setScale(2).setAlpha(light.opacity);
    const dust = `${key}:dust`, rgb = light.colour.join(',');
    radial(scene, dust, 6, 6, [[0, `rgba(${rgb},.9)`], [.25, `rgba(${rgb},.6)`], [1, `rgba(${rgb},0)`]]);
    for (let i = 0; i < light.motes; i++) this.#spawn({ kind: 'dust', from: light.from, daylight: light,
      image: this.#add(0, 0, dust, 2000, Phaser.BlendModes.SCREEN) }, -this.#random() * 12);
  }

  /** Diffuse daylight lifts cloth and metal without recolouring them as lamplight. */
  daylightAt(x: number, y: number): number {
    let exposure = 0;
    for (const light of this.#place.daylight ?? []) exposure += daylightWeight(light, x, y) * light.exposure;
    return Math.min(1, exposure);
  }

  /** The kiln's own masonry bends in the rising air. Small cached cuts stay below people and flames. */
  #heatHaze(plateId: string, index: number, heat: Heat): void {
    const scene = this.#scene, key = `${plateId}:heat:${index}`, [x, y] = heat.at, { width, height } = heat;
    let cut = this.#cuts.get(key);
    if (!cut) {
      const source = Object.assign(document.createElement('canvas'), { width, height });
      source.getContext('2d')!.drawImage(scene.textures.get(plateId).getSourceImage() as HTMLImageElement, x, y, width, height, 0, 0, width, height);
      const mask = Object.assign(document.createElement('canvas'), { width, height }), c = mask.getContext('2d')!;
      c.translate(width / 2, height / 2); c.scale(width / 2, height / 2);
      const gradient = c.createRadialGradient(0, 0, .1, 0, 0, 1);
      gradient.addColorStop(0, '#fff'); gradient.addColorStop(.6, '#fffc'); gradient.addColorStop(1, '#fff0');
      c.fillStyle = gradient; c.fillRect(-1, -1, 2, 2);
      cut = { source, mask }; this.#cuts.set(key, cut);
    }
    const texture = scene.textures.exists(key) ? scene.textures.get(key) as Phaser.Textures.CanvasTexture : scene.textures.createCanvas(key, width, height)!;
    this.#heat.push({ ...cut, texture, strength: heat.strength, image: this.#add(x, y, key, -998, Phaser.BlendModes.NORMAL).setOrigin(0) });
  }

  /** Animate luminous coals from the painting itself, inside the kiln mouth.
   * Soft tongues still supply the tip; this gives the whole fire an unsynchronised
   * rolling body without moving the arch or adding a white additive blob. */
  #hearth(plateId: string, index: number, hearth: Hearth): void {
    const box = bounds(hearth.polygon), x = Math.floor(box.left), y = Math.floor(box.top);
    const width = Math.ceil(box.right) - x, height = Math.ceil(box.bottom) - y;
    const key = `${plateId}:hearth:${index}`, scene = this.#scene;
    let cut = this.#cuts.get(key);
    if (!cut) {
      const source = Object.assign(document.createElement('canvas'), { width, height }), c = source.getContext('2d')!;
      c.drawImage(scene.textures.get(plateId).getSourceImage() as HTMLImageElement, x, y, width, height, 0, 0, width, height);
      const mask = Object.assign(document.createElement('canvas'), { width, height }), m = mask.getContext('2d')!;
      const pixels = c.getImageData(0, 0, width, height);
      // Only burning orange/yellow pixels move; the black grate and stone keep their weight.
      for (let i = 0; i < pixels.data.length; i += 4) {
        const red = pixels.data[i]!, green = pixels.data[i + 1]!, blue = pixels.data[i + 2]!;
        pixels.data[i + 3] = 255 * Math.max(0, Math.min(1, (red - 105) / 100, (red - blue - 30) / 70, (green - 35) / 80));
      }
      m.putImageData(pixels, 0, 0); m.globalCompositeOperation = 'destination-in';
      m.beginPath(); hearth.polygon.forEach(([px, py], i) => i ? m.lineTo(px - x, py - y) : m.moveTo(px - x, py - y)); m.closePath(); m.fill();
      cut = { source, mask }; this.#cuts.set(key, cut);
    }
    const texture = scene.textures.exists(key) ? scene.textures.get(key) as Phaser.Textures.CanvasTexture : scene.textures.createCanvas(key, width, height)!;
    this.#hearths.push({ ...cut, texture, strength: hearth.strength, image: this.#add(x, y, key, -997, Phaser.BlendModes.NORMAL).setOrigin(0) });
  }

  #flow(plateId: string, index: number, current: Current): void {
    const scene = this.#scene, box = bounds(current.polygon), margin = 6, speed = Math.hypot(...current.flow) || 1;
    const along: Point = [current.flow[0] / speed, current.flow[1] / speed];
    // One cycle scrolls this far: long enough to read as travel, short enough that the cross-fade hides the reset.
    const cycle = Math.max(24, Math.min(90, speed * 1.6)), swell = current.swell ?? 0;
    const scale = 1;
    const left = Math.floor(box.left - margin), top = Math.floor(box.top - margin);
    const width = Math.ceil(box.right + margin) - left, height = Math.ceil(box.bottom + margin) - top, key = `${plateId}:flow:${index}`;
    const w = Math.ceil(width * scale), h = Math.ceil(height * scale);
    let cut = this.#cuts.get(key);
    if (!cut) {
      // The source is the water alone (clipped hard to its polygon), so nothing beside it slides in; it wraps on itself.
      const outline = (c: CanvasRenderingContext2D): void => {
        c.beginPath();
        current.polygon.forEach(([x, y], i) => i ? c.lineTo((x - left) * scale, (y - top) * scale) : c.moveTo((x - left) * scale, (y - top) * scale)); c.closePath();
      };
      const exclude = (c: CanvasRenderingContext2D): void => {
        c.globalCompositeOperation = 'destination-out';
        for (const polygon of current.exclude ?? []) {
          c.beginPath(); polygon.forEach(([x, y], i) => i ? c.lineTo((x - left) * scale, (y - top) * scale) : c.moveTo((x - left) * scale, (y - top) * scale)); c.closePath(); c.fill();
        }
        c.globalCompositeOperation = 'source-over';
      };
      const source = Object.assign(document.createElement('canvas'), { width: w, height: h }), c = source.getContext('2d')!;
      const painting = scene.textures.get(plateId).getSourceImage() as HTMLImageElement;
      outline(c); c.clip(); c.drawImage(painting, -left * scale, -top * scale, painting.width * scale, painting.height * scale);
      // Feather both ends along the flow, so where the copy wraps it melts into the painting beneath rather than showing a seam.
      const reach = Math.abs(along[0]) * w + Math.abs(along[1]) * h, cx = w / 2, cy = h / 2;
      const feather = c.createLinearGradient(cx - along[0] * reach / 2, cy - along[1] * reach / 2, cx + along[0] * reach / 2, cy + along[1] * reach / 2), f = Math.min(.3, 24 / reach);
      feather.addColorStop(0, 'rgba(0,0,0,0)'); feather.addColorStop(f, '#000'); feather.addColorStop(1 - f, '#000'); feather.addColorStop(1, 'rgba(0,0,0,0)');
      c.globalCompositeOperation = 'destination-in'; c.fillStyle = feather; c.fillRect(0, 0, w, h); exclude(c);
      const mask = Object.assign(document.createElement('canvas'), { width: w, height: h }), m = mask.getContext('2d')!;
      m.filter = `blur(${4 * scale}px)`; m.fillStyle = '#fff'; outline(m); m.fill();
      // The flow fades in below its upstream edge, so a fall's lip stays the painting's own and never ghosts in the cross-fade.
      const start: Point = [(box.left - left) * scale, (box.top - top) * scale], end: Point = [(box.right - left) * scale, (box.bottom - top) * scale];
      const from: Point = [along[0] >= 0 ? start[0] : end[0], along[1] >= 0 ? start[1] : end[1]], lead = 22 * scale;
      const fade = m.createLinearGradient(from[0], from[1], from[0] + along[0] * lead, from[1] + along[1] * lead);
      fade.addColorStop(0, 'rgba(0,0,0,0)'); fade.addColorStop(1, '#000');
      m.filter = 'none'; m.globalCompositeOperation = 'destination-in'; m.fillStyle = fade; m.fillRect(0, 0, w, h);
      // The feather stays inside water. Boats, stone and columns never enter either copy.
      const hard = Object.assign(document.createElement('canvas'), { width: w, height: h }), hc = hard.getContext('2d')!;
      outline(hc); hc.fillStyle = '#fff'; hc.fill(); exclude(hc); m.drawImage(hard, 0, 0);
      cut = { source, mask }; this.#cuts.set(key, cut);
    }
    const texture = scene.textures.exists(key) ? scene.textures.get(key) as Phaser.Textures.CanvasTexture : scene.textures.createCanvas(key, w, h)!;
    // Just above the painting: under figures, foregrounds and the rest of the ambient layer, as the water it replaces is.
    const image = this.#add(left, top, key, -999, Phaser.BlendModes.NORMAL).setOrigin(0).setScale(1 / scale);
    // The copy wraps after this far along the flow: the region's own length in that direction.
    const span = Math.min(Math.abs(along[0]) > .01 ? w / Math.abs(along[0]) : Infinity, Math.abs(along[1]) > .01 ? h / Math.abs(along[1]) : Infinity);
    this.#flows.push({ image, texture, ...cut, along, cycle: cycle * scale, speed: speed * scale, swell: swell * scale, span, slow: speed < 30 });
  }

  #drawFlow(flow: Flow, t: number): void {
    const c = flow.texture.context, { width, height } = flow.texture, phase = t * flow.speed / flow.cycle, [ux, uy] = flow.along;
    const copy = (x: number, y: number): void => {
      if (!flow.swell) { c.drawImage(flow.source, x, y); return; }
      // Swell: each band of rows sways sideways a few pixels on a slow wave that travels down the water.
      for (let row = 0; row < height; row += 8) {
        const from = row - y;
        if (from < 0 || from >= height) continue;
        c.drawImage(flow.source, 0, from, width, Math.min(8, height - from), x + flow.swell * Math.sin((row + flow.image.y) * .1 - t * 1.1), row, width, Math.min(8, height - from));
      }
    };
    const layer = (shift: number): void => { copy(ux * shift, uy * shift); copy(ux * (shift - flow.span), uy * (shift - flow.span)); };
    const a = phase % 1, b = (phase + .5) % 1;
    c.globalCompositeOperation = 'source-over'; c.globalAlpha = 1; c.clearRect(0, 0, width, height);
    layer(a * flow.cycle);
    c.globalAlpha = 1 - Math.abs(2 * b - 1); layer(b * flow.cycle);
    c.globalAlpha = 1; c.globalCompositeOperation = 'destination-in'; c.drawImage(flow.mask, 0, 0);
    c.globalCompositeOperation = 'source-over';
    flow.texture.refresh();
  }

  /** Light what the story has burned here (ids from `burnings`). Fire, once lit, burns until the painting changes. */
  show(ids: readonly string[]): void {
    const random = this.#random, between = (a: number, b: number): number => a + (b - a) * random();
    for (const burning of this.#place.burning ?? []) {
      if (!ids.includes(burning.id) || this.#lit.has(burning.id)) continue;
      this.#lit.add(burning.id);
      for (const at of burning.flames) {
        const base: Point = [at[0], at[1] - 10 * at[2]];
        // A burning heap: charred where it stands, several fires rather than one lamp, and a dark smoke column.
        this.#add(at[0], at[1], 'ambient-scorch', 2000, Phaser.BlendModes.NORMAL).setScale(at[2] * 1.3, at[2]);
        for (let i = 0; i < 5; i++) this.#flame([at[0] + between(-16, 16) * at[2], at[1] + between(-4, 4), at[2] * between(.6, 1.3)]);
        this.#lamps.push({ image: this.#add(base[0], base[1], 'painted-glow', 2000).setScale(at[2] * .9).setAlpha(.3), base: .3, scale: at[2] * .9, swing: 2.4,
          rates: [between(2, 3.4), between(5, 8), between(31, 53)], phases: [between(0, 6.3), between(0, 6.3), between(0, 6.3)], gutter: 0 });
        for (let i = 0; i < 4 + at[2] * 3; i++) this.#spawn({ kind: 'ember', from: base, image: this.#add(base[0], base[1], 'ambient-ember', 2002) }, -between(0, 1.8));
        for (let i = 0; i < 4; i++) this.#spawn({ kind: 'puff', from: [base[0], base[1] - 14 * at[2]], image: this.#add(base[0], base[1], 'ambient-smoke', 2002, Phaser.BlendModes.NORMAL) }, -i * 1.2 - between(0, .6));
      }
    }
    this.tick(0, this.#still, true);
  }

  /** A flame of five tongues: a red outer, two orange licks either side, a yellow body and a pale core, each on its own noise. */
  #flame([x, y, size]: Flame): void {
    const random = this.#random, between = (a: number, b: number): number => a + (b - a) * random();
    const s = size * .75;
    for (const [colour, dx, width, height, alpha] of [[0, 0, 1.25, 1, .55], [1, -.3, .75, .8, .6], [1, .32, .7, .72, .6], [2, 0, .7, .62, .75], [3, 0, .38, .34, .8]] as const) {
      this.#tongues.push({ image: this.#add(x, y, `ambient-flame-${colour}`, 2002).setOrigin(.5, .97), x: x + dx * 10 * s, y, width: width * s, height: height * s, alpha,
        rates: [between(6, 9), between(10, 15), between(2.2, 3.4)], phases: [between(0, 6.3), between(0, 6.3), between(0, 6.3)] });
    }
  }

  #spawn(particle: Pick<Particle, 'kind' | 'from' | 'image'> & { polygon?: readonly Point[]; fall?: Fall; daylight?: Daylight; plume?: Plume }, age: number): void {
    const p = particle as Particle;
    p.age = age; p.phase = this.#random() * 6.3;
    this.#reset(p);
    this.#particles.push(p);
  }

  #reset(p: Particle): void {
    const random = this.#random, between = (a: number, b: number): number => a + (b - a) * random();
    const [fx, fy] = p.from;
    if (p.kind === 'ember') {
      p.x = fx + between(-4, 4); p.y = fy - between(0, 6); p.vx = between(-5, 7); p.vy = -between(20, 38);
      p.life = between(.9, 2); p.size = between(.45, .9); p.peak = between(.55, .95);
    } else if (p.kind === 'puff') {
      p.x = fx + between(-(p.plume?.spread ?? 2), p.plume?.spread ?? 2); p.y = fy - 4; p.vx = between(2, 6); p.vy = -between(8, 13);
      p.life = between(4.2, 5.6); p.size = between(.3, .45); p.peak = between(.2, .3);
      if (p.plume) { p.vx = p.plume.drift[0] * between(.8, 1.2); p.vy = p.plume.drift[1] * between(.8, 1.2); p.size = p.plume.size * between(.8, 1.1); p.life = between(7, 9); p.peak = p.plume.opacity ?? .32; }
    } else if (p.kind === 'mote') {
      p.x = fx + between(-80, 80); p.y = fy + between(-40, 70); p.vx = between(-5, 5); p.vy = between(-4, 3);
      p.life = between(6, 10); p.size = between(.5, .85);
      p.peak = .75 * Math.max(0, 1 - Math.hypot(p.x - fx, (p.y - fy) * 1.3) / 110);
    } else if (p.kind === 'dust') {
      const light = p.daylight!, along = between(.1, .9), dx = light.to[0] - fx, dy = light.to[1] - fy, length = Math.hypot(dx, dy);
      const across = between(-.8, .8) * (light.width[0] + (light.width[1] - light.width[0]) * along);
      p.x = fx + dx * along - dy / length * across; p.y = fy + dy * along + dx / length * across;
      p.vx = between(-9, -3); p.vy = between(2, 5); p.life = between(8, 14); p.size = between(.3, .65); p.peak = between(.45, .85);
    } else if (p.kind === 'drop') {
      // vx holds the drop's place across the fall's width; its path is drawn in `tick`.
      p.vx = between(-.5, .5) * p.fall!.width; p.life = p.fall!.kind === 'water' ? between(.55, .8) : between(.8, 1.1);
      p.size = between(.6, 1.1); p.peak = p.fall!.kind === 'water' ? between(.28, .48) : between(.55, .85);
    } else if (p.kind === 'foam') {
      const water = p.fall!.kind === 'water';
      p.x = fx + between(-.6, .6) * p.fall!.width; p.y = fy + between(-4, 6); p.vx = between(-10, 10); p.vy = -between(2, water ? 12 : 5);
      p.life = between(1, 1.8); p.size = between(.3, .5) * (water ? 1 : .8); p.peak = between(.3, .5) * (water ? 1 : .6);
    } else {
      const box = bounds(p.polygon!);
      p.x = box.left; p.y = box.top;
      for (let i = 0; i < 20; i++) {
        const x = between(box.left, box.right), y = between(box.top, box.bottom);
        if (inside([x, y], p.polygon!)) { p.x = x; p.y = y; break; }
      }
      p.vx = between(-2, 2); p.vy = 0; p.life = between(.5, 1.3); p.size = between(.5, 1.2); p.peak = between(.3, .6);
    }
  }

  /** Advance by `step` ms (0 while time is held). `still` is reduced motion: steady lamps, no particles. */
  tick(step: number, still: boolean, force = false): void {
    if (!force && step === 0 && still === this.#still) return;
    this.#still = still;
    const dt = step / 1000, t = this.#time += dt;
    for (const moth of this.#moths) {
      const p = moth.phase, orbit = t * 1.8 + p;
      moth.image.setVisible(!still).setPosition(moth.at[0] + Math.sin(orbit) * 13 + Math.sin(t * 4.1 + p) * 3,
        moth.at[1] - 9 + Math.cos(orbit * 1.3) * 9).setRotation(Math.sin(orbit + .8) * .8)
        .setScale(.18 + Math.abs(Math.sin(t * 37 + p)) * .22, .35);
    }
    for (const lamp of this.#lamps) {
      if (still) { lamp.image.setAlpha(lamp.base).setScale(lamp.scale); continue; }
      const [a, b, c] = lamp.rates, [pa, pb, pc] = lamp.phases;
      if (lamp.gutter > 0) lamp.gutter = Math.max(0, lamp.gutter - dt * .25);
      else if (this.#random() < dt * .05) lamp.gutter = .1;
      const breath = Math.sin(t * a + pa);
      const alpha = lamp.base + lamp.swing * (.055 * breath + .03 * Math.sin(t * b + pb) + .018 * Math.sin(t * c + pc)) - lamp.gutter;
      lamp.image.setAlpha(Math.max(.04, alpha)).setScale(lamp.scale * (1 + .05 * breath));
    }
    for (const p of this.#particles) {
      if (still) { p.image.setVisible(false); continue; }
      p.age += dt;
      if (p.age >= p.life) { p.age -= p.life; this.#reset(p); }
      if (p.age < 0) { p.image.setVisible(false); continue; }
      const u = p.age / p.life;
      if (p.kind === 'drop') {
        // Accelerating down the fall, stretched with speed, fading in at the lip and out into the foam.
        const { from, to } = p.fall!, dx = to[0] - from[0], dy = to[1] - from[1], length = Math.hypot(dx, dy), along = u * (.45 + .55 * u);
        p.image.setVisible(true).setPosition(from[0] + dx * along - dy / length * p.vx, from[1] + dy * along + dx / length * p.vx)
          .setRotation(Math.atan2(dy, dx) - Math.PI / 2).setAlpha(p.peak * Math.sqrt(Math.sin(Math.PI * u))).setScale(p.size, p.size * (.6 + 1.1 * u));
        continue;
      }
      p.x += p.vx * dt; p.y += p.vy * dt;
      let alpha = p.peak, scale = p.size, x = p.x;
      if (p.kind === 'ember') { alpha *= Math.sqrt(Math.sin(Math.PI * u)); x += Math.sin(p.age * 5 + p.phase) * 3; p.vy *= 1 - dt * .4; }
      else if (p.kind === 'puff') { alpha *= u < .3 ? u / .3 : (1 - u) / .7; scale *= 1 + u * 2.6; x += Math.sin(p.age * .9 + p.phase) * 3; }
      else if (p.kind === 'mote') { alpha *= Math.sin(Math.PI * u); x += Math.sin(p.age * .7 + p.phase) * 4; }
      else if (p.kind === 'dust') { x += Math.sin(p.age * .5 + p.phase) * 6; alpha *= Math.sin(Math.PI * u) * daylightWeight(p.daylight!, x, p.y); }
      else if (p.kind === 'foam') { alpha *= u < .15 ? u / .15 : (1 - u) / .85; scale *= 1 + u * 1.8; p.vx *= 1 - dt * 1.5; }
      else alpha *= Math.sin(Math.PI * u) ** 2;
      p.image.setVisible(true).setPosition(x, p.y).setAlpha(alpha).setScale(scale, p.kind === 'glint' ? 1 : scale);
    }
    // Still: the painting shows through untouched.
    // Off-camera water is not redrawn, and slow water only every other frame: it moves under a pixel a frame.
    // Slow regions take turns, so their redraws split across the two frames.
    const view = this.#scene.cameras.main.worldView, odd = (this.#frame = this.#frame + 1 | 0) & 1;
    this.#hearths.forEach((hearth, index) => {
      hearth.image.setVisible(!still);
      if (still || (index & 1) === odd || hearth.image.x > view.right || hearth.image.x + hearth.source.width < view.x || hearth.image.y > view.bottom || hearth.image.y + hearth.source.height < view.y) return;
      const c = hearth.texture.context, { width, height } = hearth.source;
      c.clearRect(0, 0, width, height);
      for (let y = 0; y < height; y += 4) {
        const root = Math.sin(Math.PI * y / height);
        const bend = hearth.strength * root * Math.sin(y * .17 - t * 5.3 + index * 1.9);
        c.globalAlpha = .65 + .25 * Math.sin(t * 3.7 + y * .21 + index);
        c.drawImage(hearth.source, 0, y, width, Math.min(4, height - y), bend, y - root * (1 + Math.sin(t * 4.1 + index)), width, Math.min(4, height - y));
      }
      c.globalAlpha = 1; c.globalCompositeOperation = 'destination-in'; c.drawImage(hearth.mask, 0, 0); c.globalCompositeOperation = 'source-over'; hearth.texture.refresh();
    });
    this.#heat.forEach((heat, index) => {
      heat.image.setVisible(!still);
      if (still || (index & 1) === odd || heat.image.x > view.right || heat.image.x + heat.source.width < view.x || heat.image.y > view.bottom || heat.image.y + heat.source.height < view.y) return;
      const c = heat.texture.context, { width, height } = heat.source;
      c.clearRect(0, 0, width, height);
      for (let y = 0; y < height; y += 4) {
        const bend = heat.strength * Math.sin(y * .11 - t * 3.4 + index) * Math.sin(Math.PI * y / height);
        c.drawImage(heat.source, 0, y, width, Math.min(4, height - y), bend, y, width, Math.min(4, height - y));
      }
      c.globalCompositeOperation = 'destination-in'; c.drawImage(heat.mask, 0, 0); c.globalCompositeOperation = 'source-over'; heat.texture.refresh();
    });
    this.#flows.forEach((flow, index) => {
      flow.image.setVisible(!still);
      if (!still && !(flow.slow && (index & 1) === odd) && flow.image.x < view.right && flow.image.x + flow.image.displayWidth > view.x && flow.image.y < view.bottom && flow.image.y + flow.image.displayHeight > view.y) this.#drawFlow(flow, t);
    });
    for (const tongue of this.#tongues) {
      const [a, b, c] = tongue.rates, [pa, pb, pc] = tongue.phases;
      // Height flickers fast with the odd tall lick; the tip bends slowly from a fixed root. Still: each at rest.
      const flicker = still ? 0 : .6 * Math.sin(t * a + pa) + .4 * Math.sin(t * b + pb), lick = still ? 0 : Math.max(0, Math.sin(t * b * .37 + pc)) ** 3;
      const bend = still ? 0 : Math.sin(t * c + pc) + .35 * Math.sin(t * a * .5 + pb);
      tongue.image.setPosition(tongue.x + bend * tongue.width * 1.5, tongue.y).setAngle(bend * 9)
        .setScale(tongue.width * (1 - .18 * flicker), tongue.height * (1 + .28 * flicker + .45 * lick)).setAlpha(tongue.alpha * (.8 + .2 * flicker));
    }
    for (const ripple of this.#ripples) {
      if (still) { ripple.image.setAlpha(.8).setX(ripple.left); continue; }
      ripple.image.setAlpha(.78 + .22 * Math.sin(t * 2.3 / (1 + ripple.index * .28) + ripple.index)).setX(ripple.left + Math.sin(t * .35 + ripple.index) * 2.5);
    }
    for (const drift of this.#mist) {
      const u = still ? drift.offset : (drift.offset + t * drift.speed / drift.span) % 1;
      drift.image.setPosition(drift.left + u * drift.span, drift.y).setAlpha(drift.alpha * Math.sin(Math.PI * u));
    }
  }
}
